import { Signal } from '@preact/signals'
import { Text } from 'preact-i18n'
import { Transition, Header, List, Icon, Segment } from 'semantic-ui-react'
import { LabelMenuOptionsOverlayContainer, Switch } from '../../atoms'
import {
  LABEL_PRESET_OPTIONS,
  DATE_FORMAT_OPTIONS,
  type LabelPreset,
  type DateFormat,
} from '../../../store/LabelCaptureSettingsStore'

function handleListKeyDown(e: KeyboardEvent, onSelect: (value: string) => void, value: string) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    onSelect(value)
  } else if (e.key === 'ArrowDown') {
    e.preventDefault()
    const next = (e.currentTarget as HTMLElement).nextElementSibling as HTMLElement | null
    next?.focus()
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    const prev = (e.currentTarget as HTMLElement).previousElementSibling as HTMLElement | null
    prev?.focus()
  }
}

const LabelMenuOptionOverlay = ({
  open,
  selectedPreset,
  selectedDateFormat,
  adaptiveRecognition,
  acceptPartialDates,
  pricesIncludeDecimals,
  onPresetChange,
  onDateFormatChange,
  onAdaptiveRecognitionChange,
  onAcceptPartialDatesChange,
  onPricesIncludeDecimalsChange,
}: {
  open: boolean
  selectedPreset: Signal<LabelPreset>
  selectedDateFormat: Signal<DateFormat>
  adaptiveRecognition: Signal<boolean>
  acceptPartialDates: Signal<boolean>
  pricesIncludeDecimals: Signal<boolean>
  onPresetChange: (value: string) => void
  onDateFormatChange: (value: string) => void
  onAdaptiveRecognitionChange: (value: boolean) => void
  onAcceptPartialDatesChange: (value: boolean) => void
  onPricesIncludeDecimalsChange: (value: boolean) => void
}) => {
  return (
    <Transition visible={open} animation="fade" duration={250}>
      <LabelMenuOptionsOverlayContainer>
        <Header as="h3" inverted>
          <Text id="labelCapture.scanModeConfiguration" />
        </Header>
        <Segment inverted>
          <Header as="h5" inverted id="label-scenario-heading">
            <Text id="labelCapture.scenario" />
          </Header>
          <List divided relaxed inverted selection role="listbox" aria-labelledby="label-scenario-heading">
            {LABEL_PRESET_OPTIONS.map((option) => (
              <List.Item
                key={option.key}
                role="option"
                aria-selected={selectedPreset.value === option.value}
                tabIndex={0}
                onClick={() => onPresetChange(option.value)}
                onKeyDown={(e: KeyboardEvent) => handleListKeyDown(e, onPresetChange, option.value)}
                style={{ padding: '0.8rem 0.5rem' }}
              >
                <List.Content floated="right">
                  {selectedPreset.value === option.value && <Icon name="check" color="teal" aria-hidden="true" />}
                </List.Content>
                <List.Content>{option.text}</List.Content>
              </List.Item>
            ))}
          </List>
          <Header as="h5" inverted id="date-format-heading">
            <Text id="labelCapture.expectedDateFormat" />
          </Header>
          <List divided relaxed inverted selection role="listbox" aria-labelledby="date-format-heading">
            {DATE_FORMAT_OPTIONS.map((option) => (
              <List.Item
                key={option.key}
                role="option"
                aria-selected={selectedDateFormat.value === option.value}
                tabIndex={0}
                onClick={() => onDateFormatChange(option.value)}
                onKeyDown={(e: KeyboardEvent) => handleListKeyDown(e, onDateFormatChange, option.value)}
                style={{ padding: '0.8rem 0.5rem' }}
              >
                <List.Content floated="right">
                  {selectedDateFormat.value === option.value && <Icon name="check" color="teal" aria-hidden="true" />}
                </List.Content>
                <List.Content>{option.text}</List.Content>
              </List.Item>
            ))}
          </List>
        </Segment>
        <Header as="h3" inverted>
          <Text id="labelCapture.additionalSettings" />
        </Header>
        <Segment inverted style={{ textAlign: 'start' }}>
          <Switch
            label={
              <label htmlFor="acceptPartialDates">
                <Text id="labelCapture.acceptPartialDates" />
              </label>
            }
            id="acceptPartialDates"
            checked={acceptPartialDates.value}
            onChange={() => onAcceptPartialDatesChange(!acceptPartialDates.value)}
          />
          <p style={{ marginTop: '0.8rem', opacity: 0.7, fontSize: '0.85rem' }}>
            <Text id="labelCapture.acceptPartialDatesDescription" />
          </p>
          <Switch
            label={
              <label htmlFor="adaptiveRecognition">
                <Text id="labelCapture.adaptiveRecognition" />
              </label>
            }
            id="adaptiveRecognition"
            checked={adaptiveRecognition.value}
            onChange={() => onAdaptiveRecognitionChange(!adaptiveRecognition.value)}
          />
          <p style={{ marginTop: '0.8rem', opacity: 0.7, fontSize: '0.85rem' }}>
            <Text id="labelCapture.adaptiveRecognitionDescription" />
          </p>
          <Switch
            label={
              <label htmlFor="pricesIncludeDecimals">
                <Text id="labelCapture.pricesIncludeDecimals" />
              </label>
            }
            id="pricesIncludeDecimals"
            checked={pricesIncludeDecimals.value}
            onChange={() => onPricesIncludeDecimalsChange(!pricesIncludeDecimals.value)}
          />
          <p style={{ marginTop: '0.8rem', opacity: 0.7, fontSize: '0.85rem' }}>
            <Text id="labelCapture.pricesIncludeDecimalsDescription" />
          </p>
        </Segment>
      </LabelMenuOptionsOverlayContainer>
    </Transition>
  )
}

export default LabelMenuOptionOverlay
